import { useCallback, useMemo } from "react";

import type { UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import type { IUserKeywordGroup } from "../keywordGroups.types";
import { keywordGroupsOptions } from "../queries/keywordGroupsQueries";

const EMPTY_KEYWORD_GROUPS: IUserKeywordGroup[] = [];

/**
 * @deprecated Use useKeywordGroupsApiClient from pro-api-client instead.
 * This hook will be removed in a future version.
 * New implementations should use the API client version for better type safety and consistency.
 */
export const useKeywordGroups = (
    options?: Omit<UseQueryOptions<IUserKeywordGroup[]>, "queryKey" | "queryFn">,
): UseQueryResult<IUserKeywordGroup[]> => {
    return useQuery({
        ...keywordGroupsOptions.user(),
        ...options,
    });
};

/**
 * @deprecated Use useSharedKeywordGroupsApiClient from pro-api-client instead.
 * This hook will be removed in a future version.
 * New implementations should use the API client version for better type safety and consistency.
 */
export const useSharedKeywordGroups = (
    options?: Omit<UseQueryOptions<IUserKeywordGroup[]>, "queryKey" | "queryFn">,
): UseQueryResult<IUserKeywordGroup[]> => {
    return useQuery({
        ...keywordGroupsOptions.shared(),
        ...options,
    });
};

/**
 * @deprecated This hook uses the legacy API structure.
 * Consider using useKeywordGroupsApiClient and filtering by ID instead.
 * This hook will be removed in a future version.
 */
export const useKeywordGroupById = (
    id: string,
    options?: Omit<UseQueryOptions<IUserKeywordGroup | null>, "queryKey" | "queryFn" | "enabled">,
): UseQueryResult<IUserKeywordGroup | null> => {
    return useQuery({
        ...keywordGroupsOptions.byId(id),
        ...options,
    });
};

/**
 * @deprecated Use useKeywordGroupsApiClient from pro-api-client instead.
 * This hook will be removed in a future version.
 * The API client version returns a structured response with OwnerGroups and SharedGroups.
 */
export const useAllKeywordGroups = (options?: {
    userGroupsOptions?: Omit<UseQueryOptions<IUserKeywordGroup[]>, "queryKey" | "queryFn">;
    sharedGroupsOptions?: Omit<UseQueryOptions<IUserKeywordGroup[]>, "queryKey" | "queryFn">;
}): {
    userGroups: IUserKeywordGroup[];
    sharedGroups: IUserKeywordGroup[];
    isLoading: boolean;
    refetch: () => Promise<void>;
    userGroupsQuery: UseQueryResult<IUserKeywordGroup[]>;
    sharedGroupsQuery: UseQueryResult<IUserKeywordGroup[]>;
} => {
    const userGroupsQuery = useKeywordGroups(options?.userGroupsOptions);
    const sharedGroupsQuery = useSharedKeywordGroups(options?.sharedGroupsOptions);

    const userGroups = userGroupsQuery.data ?? EMPTY_KEYWORD_GROUPS;
    const sharedGroups = sharedGroupsQuery.data ?? EMPTY_KEYWORD_GROUPS;
    const isLoading = userGroupsQuery.isLoading || sharedGroupsQuery.isLoading;

    const refetch = useCallback(async () => {
        await Promise.all([userGroupsQuery.refetch(), sharedGroupsQuery.refetch()]);
    }, [userGroupsQuery, sharedGroupsQuery]);

    return useMemo(
        () => ({
            userGroups,
            sharedGroups,
            isLoading,
            refetch,
            userGroupsQuery,
            sharedGroupsQuery,
        }),
        [userGroups, sharedGroups, isLoading, refetch, userGroupsQuery, sharedGroupsQuery],
    );
};
